import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["input", "meta", "label", "options", "multi", "searchable", "fullWidth", "renderType", "delay"],
  _excluded2 = ["label", "input", "meta", "noErrorMessage"],
  _excluded3 = ["label", "input"],
  _excluded4 = ["input", "meta", "labels"],
  _excluded5 = ["input", "meta", "label"],
  _excluded6 = ["input", "meta", "radioOptions"],
  _excluded7 = ["label", "input", "meta", "onSelection", "config"];
import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown, TextInput, Switch, Checkbox, Text, Radios, SearchableInput } from '@tekion/tmatter';
import _debounce from 'lodash/debounce';
import _isNumber from 'lodash/isNumber';
import TextAreaAutoSize from '../atoms/TextAreaAutoSize';
import PropertyControlledComponent from './PropertyControlledComponent';
export var renderErrorForField = function renderErrorForField(_ref) {
  var touched = _ref.touched,
    error = _ref.error;
  return /*#__PURE__*/React.createElement(PropertyControlledComponent, {
    controllerProperty: touched
  }, /*#__PURE__*/React.createElement(PropertyControlledComponent, {
    controllerProperty: error
  }, /*#__PURE__*/React.createElement(Text, {
    color: "vermilion",
    size: 6
  }, error)));
};
renderErrorForField.propTypes = {
  touched: PropTypes.bool,
  error: PropTypes.object
};
renderErrorForField.defaultProps = {
  touched: false,
  error: {}
};
export var renderDropdownOption = function renderDropdownOption(_ref2) {
  var value = _ref2.value,
    label = _ref2.label;
  return /*#__PURE__*/React.createElement(Dropdown.MenuItem, {
    key: value,
    value: value,
    label: label
  });
};
renderDropdownOption.propTypes = {
  value: PropTypes.oneOf([PropTypes.string, PropTypes.integer]).isRequired,
  label: PropTypes.oneOf([PropTypes.string, PropTypes.integer]).isRequired
};
export var renderDropdown = function renderDropdown(_ref3) {
  var _ref3$input = _ref3.input,
    value = _ref3$input.value,
    onChange = _ref3$input.onChange,
    meta = _ref3.meta,
    label = _ref3.label,
    options = _ref3.options,
    _ref3$multi = _ref3.multi,
    multi = _ref3$multi === void 0 ? false : _ref3$multi,
    _ref3$searchable = _ref3.searchable,
    searchable = _ref3$searchable === void 0 ? false : _ref3$searchable,
    _ref3$fullWidth = _ref3.fullWidth,
    fullWidth = _ref3$fullWidth === void 0 ? false : _ref3$fullWidth,
    _ref3$renderType = _ref3.renderType,
    renderType = _ref3$renderType === void 0 ? 'field' : _ref3$renderType,
    _ref3$delay = _ref3.delay,
    delay = _ref3$delay === void 0 ? 0 : _ref3$delay,
    rest = _objectWithoutProperties(_ref3, _excluded);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Dropdown, _extends({
    multi: multi,
    searchable: searchable,
    fullWidth: fullWidth,
    renderType: renderType,
    selected: value,
    label: label,
    onSelection: _isNumber(delay) ? _debounce(onChange, delay) : onChange
  }, rest), options.map(renderDropdownOption)), renderErrorForField(meta));
};
renderDropdown.propTypes = {
  options: PropTypes.array,
  label: PropTypes.string
};
renderDropdown.defaultProps = {
  options: [],
  label: ''
};
export var renderTextInput = function renderTextInput(_ref4) {
  var label = _ref4.label,
    input = _ref4.input,
    _ref4$meta = _ref4.meta,
    touched = _ref4$meta.touched,
    error = _ref4$meta.error,
    _ref4$noErrorMessage = _ref4.noErrorMessage,
    noErrorMessage = _ref4$noErrorMessage === void 0 ? false : _ref4$noErrorMessage,
    rest = _objectWithoutProperties(_ref4, _excluded2);
  var value = input.value,
    onChange = input.onChange;
  var modError = noErrorMessage ? !!(touched && error) : touched && error;
  return /*#__PURE__*/React.createElement(TextInput, _extends({
    label: label,
    value: value,
    error: modError,
    onChange: onChange
  }, input, rest));
};
export var renderTextAreaInputAutoSize = function renderTextAreaInputAutoSize(_ref5) {
  var label = _ref5.label,
    input = _ref5.input,
    rest = _objectWithoutProperties(_ref5, _excluded3);
  return /*#__PURE__*/React.createElement(TextAreaAutoSize, _extends({}, input, rest));
};
export var renderSwitch = function renderSwitch(_ref6) {
  var _ref6$input = _ref6.input,
    value = _ref6$input.value,
    onChange = _ref6$input.onChange,
    meta = _ref6.meta,
    labels = _ref6.labels,
    rest = _objectWithoutProperties(_ref6, _excluded4);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Switch, _extends({
    on: value,
    accessibleLabels: labels,
    onToggle: onChange
  }, rest)), renderErrorForField(meta));
};
export var renderCheckbox = function renderCheckbox(_ref7) {
  var _ref7$input = _ref7.input,
    value = _ref7$input.value,
    onChange = _ref7$input.onChange,
    meta = _ref7.meta,
    label = _ref7.label,
    rest = _objectWithoutProperties(_ref7, _excluded5);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Checkbox, _extends({
    checked: value,
    label: label,
    onToggle: onChange
  }, rest)), renderErrorForField(meta));
};
export var renderRadioGroup = function renderRadioGroup(_ref8) {
  var _ref8$input = _ref8.input,
    value = _ref8$input.value,
    onChange = _ref8$input.onChange,
    meta = _ref8.meta,
    radioOptions = _ref8.radioOptions,
    rest = _objectWithoutProperties(_ref8, _excluded6);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Radios, _extends({
    onChange: onChange
  }, rest), radioOptions.map(function (_ref9) {
    var radioValue = _ref9.value,
      label = _ref9.label,
      text = _ref9.text;
    return /*#__PURE__*/React.createElement(Radios.Radio, {
      key: radioValue,
      value: radioValue,
      label: label,
      text: text,
      checked: radioValue === value
    });
  })), renderErrorForField(meta));
};
export var renderSearchableInput = function renderSearchableInput(_ref10) {
  var label = _ref10.label,
    onChange = _ref10.input.onChange,
    _ref10$meta = _ref10.meta,
    touched = _ref10$meta.touched,
    error = _ref10$meta.error,
    _onSelection = _ref10.onSelection,
    config = _ref10.config,
    rest = _objectWithoutProperties(_ref10, _excluded7);
  return /*#__PURE__*/React.createElement(SearchableInput, _extends({
    id: config.label,
    error: touched && error,
    onSelection: function onSelection(value) {
      onChange(value);
      if (_onSelection) _onSelection(value);
    },
    config: config
  }, rest));
};